<script setup lang="ts">
import { computed, nextTick } from 'vue';
import { useLocale } from '@/plugins/translation';
import { useCustomerMgmt } from '@ilteducation/customer-mgmt';
import type { Context } from '@ilteducation/customer-mgmt';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = withDefaults(
  defineProps<{
    context: Context;
    preSelectedIds?: string[];
  }>(),
  {
    preSelectedIds: () => [],
  },
);
const emit = defineEmits<{ (e: 'do-search'): void }>();
const { t, l } = useLocale();
const { searchField, setSearchField } = useCustomerMgmt({
  context: props.context,
  preSelectedIds: props.preSelectedIds,
});

const searchValue = computed({
  get: () => searchField.value,
  set: (value) => {
    if (value !== undefined) {
      setSearchField(value);
    }
  },
});

const search = () => {
  emit('do-search');
};

const onClear = async () => {
  await nextTick();
  emit('do-search');
};
</script>

<template>
  <v-text-field
    v-model="searchValue"
    :label="t(l.search.search)"
    single-line
    hide-details
    clearable
    density="compact"
    variant="outlined"
    flat
    @click:clear="onClear"
    @keyup.enter="search"
  >
    <template #append-inner>
      <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" class="cursor-pointer" @click="search" />
    </template>
  </v-text-field>
</template>

<style lang="scss" scoped>
//@import '~vuetify/src/styles/settings/_variables';

:deep(input) {
  margin-left: 10px;
}

.v-input {
  :deep(.v-input__control) {
    fieldset {
      margin: auto !important;
      padding: 0 !important;
      border: 1px solid var(--v-gray-lighten2) !important;
    }
    .v-input_icon--search {
      height: 44px !important;
      width: 44px !important;
      margin: 0px -10px 0 4px;
      border-radius: 0 4px 4px 0;

      border-left: 1px solid var(--v-gray-lighten2);

      button {
        height: 100%;
        width: 100%;
      }
    }
    .v-input__icon--clear,
    .v-input__icon--search {
      button {
        color: var(--v-secondary-base) !important;
      }
    }
  }
  &--is-focused {
    :deep(.v-input__control) {
      fieldset {
        border-color: rgb(var(--v-theme-secondary)) !important;
      }
      .v-input__icon--search {
        border-color: rgb(var(--v-theme-secondary));
      }
    }
  }
}
</style>
